import querystring from 'querystring';
import {
  getPage,
  getRedirect,
  getAllPages,
  WagtailApiResponseError,
} from '../api/wagtail';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import LazyContainers from '../containers/LazyContainers';

const isProd = process.env.NODE_ENV === 'production';

export default function CatchAllPage({ componentName, componentProps }) {
  const Component = LazyContainers[componentName];
  if (!Component) {
    return <h1>Component {componentName} not found</h1>;
  }
  return <Component {...componentProps} />;
}

// For SSR
export async function getServerSideProps({ req, params, res, locale }) {
  res.setHeader(
    'Cache-Control',
    'public, s-maxage=300, stale-while-revalidate=299'
  );

  let path = params?.path || [];
  path = path.join('/');

  const { host } = req.headers;
  let queryParams = new URL(req.url, `https://${host}`).search;

  const hasQueries = queryParams.indexOf('?') === 0;
  if (hasQueries) {
    queryParams = queryParams.substr(1);
  }

  queryParams = querystring.parse(queryParams);

  try {
    const {
      json: { componentName, componentProps, redirect, customResponse },
      headers,
    } = await getPage(path, queryParams, {
      headers: {
        cookie: req.headers.cookie,
        host,
      },
    });

    // Forward any cookie we encounter
    const cookies = headers.get('set-cookie');
    if (cookies) {
      res.setHeader('Set-Cookie', cookies);
    }

    if (customResponse) {
      const { body, body64, contentType } = customResponse;
      res.setHeader('Content-Type', contentType);
      res.statusCode = 200;
      res.write(body64 ? Buffer.from(body64, 'base64') : body);
      res.end();
      return { props: {} };
    }

    if (redirect) {
      const { destination, isPermanent } = redirect;
      return {
        redirect: {
          destination: destination,
          permanent: isPermanent,
        },
      };
    }

    return {
      props: {
        ...(await serverSideTranslations(locale, ['common'])),
        componentName,
        componentProps,
      },
    };
  } catch (err) {
    if (!(err instanceof WagtailApiResponseError)) {
      throw err;
    }

    // When in development, show django error page on error
    if (!isProd && err.response.status >= 500) {
      const html = await err.response.text();
      return {
        props: {
          ...(await serverSideTranslations(locale, ['common'])),
          componentName: 'PureHtmlPage',
          componentProps: { html },
        },
      };
    }

    if (err.response.status >= 500) {
      throw err;
    }
  }

  // Try to serve redirect
  try {
    const { json: redirect } = await getRedirect(path, queryParams, {
      headers: {
        cookie: req.headers.cookie,
        host,
      },
    });

    if (redirect) {
      return {
        redirect: {
          destination: redirect.destination,
          permanent: redirect.isPermanent,
        },
      };
    }
  } catch (err) {
    if (!(err instanceof WagtailApiResponseError)) {
      throw err;
    }

    if (err.response.status >= 500) {
      throw err;
    }
  }

  // Serve 404 page
  return {
    notFound: true,
  };
}
